import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../css/Reports/headersection_report.css";
import "../../css/Reports/reportlistsection_report.css";
import "../../css/Reports/leftpanel_report.css";
import "../../css/Reports/rightpanel_report.css";
import ReportHeader from "./ReportHeader";
import ReportLeftPanel from "./ReportLeftPanel";
import ReportListSection from "./ReportListSection";
import ReportRightPanel from "./ReportRightPanel";
import { getReportListApi } from "../../api/reportApi";
import { GetParameters } from "../../api/getParameter";

export default function ReportPage() {
    const navigate = useNavigate();
    const [reports, setReports] = useState([]);
    const [sidebarPinned, setSidebarPinned] = useState(true);
    const [sidebarHover, setSidebarHover] = useState(false);
    const [groupByOptions, setGroupByOptions] = useState([]);
    const [groupBySelected, setGroupBySelected] = useState([]);
    const [selectedReportPath, setSelectedReportPath] = useState("");
    const [reportType, setReportType] = useState("summary");
    const [noGrouping, setNoGrouping] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);

    // Dynamic Options lists for dropdowns
    const [paramOptions, setParamOptions] = useState({
        categories: [],
        metals: [],
        goldQualities: [],
        sizes: [],
        diamondShapes: [],
        diamondQualities: [],
        collections: []
    });
    const [customers, setCustomers] = useState([]);

    // Filters State
    const [filters, setFilters] = useState({
        fromDate: null,
        toDate: null,
        styleNo: "",
        customer: [],
        category: [],
        metal: [],
        goldQuality: [],
        price: "",
        diamondShape: [],
        diamondQuality: [],
        itemSize: [],
        collection: []
    });

    useEffect(() => {
        loadReports();
        fetchParams();
        loadLocalCustomers();
    }, []);

    const loadLocalCustomers = () => {
        try {
            const localCustomers = JSON.parse(localStorage.getItem("customerList") || "[]");
            const mapped = localCustomers
                .filter(c => {
                    const name = (c.FirstName || c.CustomerName || c.CUSTOMERNAME || "").trim().toLowerCase();
                    const id = String(c.Customer_ID || c.Party_ID || c.PARTY_ID || "").trim();
                    return name !== "all customer" && name !== "all customers" && name !== "all" && id !== "0" && id !== "";
                })
                .map(c => ({
                    label: c.FirstName || c.CustomerName || c.CUSTOMERNAME || "Unknown",
                    value: String(c.Customer_ID || c.Party_ID || c.PARTY_ID || "")
                }));
            setCustomers(mapped);
        } catch (err) {
            console.error("Error loading local customers:", err);
        }
    };

    const fetchParams = async () => {
        try {
            const res = await GetParameters({ CategoryList: "" });
            const params = res?.Parameters || [];
            
            const categories = params
                .filter(p => ["PRODUCTCATEGORY", "CATEGORY", "STYLE"].includes(p.PARATYPE?.toUpperCase()))
                .map(p => ({ label: p.PARANAME, value: String(p.PARA_ID) }));
                
            const metals = params
                .filter(p => ["METALTONE", "METAL"].includes(p.PARATYPE?.toUpperCase()))
                .map(p => ({ label: p.PARANAME, value: String(p.PARA_ID) }));
                
            const goldQualities = params
                .filter(p => ["GOLDQUALITY", "GOLDKT"].includes(p.PARATYPE?.toUpperCase()))
                .map(p => ({ label: p.PARANAME, value: String(p.PARA_ID) }));
                
            const sizes = params
                .filter(p => ["PRODUCTSIZE", "ITEMSIZE"].includes(p.PARATYPE?.toUpperCase()))
                .map(p => ({ label: p.PARANAME, value: String(p.PARA_ID) }));

            const diamondShapes = params
                .filter(p => ["MATERIALTYPE", "SHAPE", "DIAMONDSHAPE"].includes(p.PARATYPE?.toUpperCase()))
                .map(p => ({ label: p.PARANAME, value: String(p.PARA_ID) }));

            const diamondQualities = params
                .filter(p => ["MATERIALQUALITY", "QUALITY", "DIAMONDQUALITY"].includes(p.PARATYPE?.toUpperCase()))
                .map(p => ({ label: p.PARANAME, value: String(p.PARA_ID) }));

            const collections = params
                .filter(p => ["COLLECTION"].includes(p.PARATYPE?.toUpperCase()))
                .map(p => ({ label: p.PARANAME, value: String(p.PARA_ID) }));

            setParamOptions({ categories, metals, goldQualities, sizes, diamondShapes, diamondQualities, collections });
        } catch (err) {
            console.error("Error fetching parameter lists:", err);
        }
    };

    const loadReports = async () => {
        const payload = { Report_ID: 1 };
        const res = await getReportListApi(payload);
        const data = res?.ReportList || [];
        const parents = data.filter(x => !x.ReportId);
        const children = data.filter(x => x.ReportId);
        const structured = parents.map(p => ({
            id: p.ReportGroupId,
            title: p.GroupTitle,
            children: children
                .filter(c => c.ReportId === p.ReportGroupId)
                .map(c => ({
                    name: c.GroupTitle,
                    groupByTag: c.GroupByTag,
                    spName: c.SpName,
                    reportType: c.ReportType
                }))
        }));

        setReports(structured);
    };

    const formatDateString = (dateVal) => {
        if (!dateVal) return "";
        const d = new Date(dateVal);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatMultiSelect = (valArray) => {
        if (!valArray || valArray.length === 0) return "";
        return valArray.join(",");
    };

    const handleGenerateReport = () => {
        if (!selectedReport || !selectedReport.spName) {
            alert("Please select a child report from the Report List sidebar first.");
            return;
        }

        const payload = {
            spName: selectedReport.spName,
            fromDate: formatDateString(filters.fromDate),
            toDate: formatDateString(filters.toDate),
            styleNo: filters.styleNo
                ? filters.styleNo
                    .split(",")
                    .map(s => s.trim())
                    .filter(Boolean)
                    .join(",")
                : "",
            customer: formatMultiSelect(filters.customer),
            category: formatMultiSelect(filters.category),
            metal: formatMultiSelect(filters.metal),
            goldQuality: formatMultiSelect(filters.goldQuality),
            price: filters.price,
            diamondShape: formatMultiSelect(filters.diamondShape),
            diamondQuality: formatMultiSelect(filters.diamondQuality),
            itemSize: formatMultiSelect(filters.itemSize),
            collection: formatMultiSelect(filters.collection),
            groupBy: noGrouping ? "" : groupBySelected.map(o => o.value).join(","),
            reportType: noGrouping ? "D" : "S"
        };

        const appliedFilters = [];
        if (filters.fromDate) {
            appliedFilters.push(`From Date: ${formatDateString(filters.fromDate)}`);
        }
        if (filters.toDate) {
            appliedFilters.push(`To Date: ${formatDateString(filters.toDate)}`);
        }
        if (filters.styleNo && filters.styleNo.trim().length > 0) {
            appliedFilters.push(`Style No: ${filters.styleNo}`);
        }
        if (filters.price) {
            appliedFilters.push(`Price: ${filters.price}`);
        }

        const getLabels = (selectedIds, options) => {
            if (!selectedIds || selectedIds.length === 0) return null;
            return selectedIds
                .map(id => {
                    const opt = options.find(o => o.value === String(id));
                    return opt ? opt.label : id;
                })
                .join(", ");
        };

        const customerLabels = getLabels(filters.customer, customers);
        if (customerLabels) appliedFilters.push(`Customer: ${customerLabels}`);

        const categoryLabels = getLabels(filters.category, paramOptions.categories);
        if (categoryLabels) appliedFilters.push(`Category: ${categoryLabels}`);

        const metalLabels = getLabels(filters.metal, paramOptions.metals);
        if (metalLabels) appliedFilters.push(`Metal: ${metalLabels}`);

        const goldQualityLabels = getLabels(filters.goldQuality, paramOptions.goldQualities);
        if (goldQualityLabels) appliedFilters.push(`Gold Quality: ${goldQualityLabels}`);

        const diamondShapeLabels = getLabels(filters.diamondShape, paramOptions.diamondShapes);
        if (diamondShapeLabels) appliedFilters.push(`Diamond Shape: ${diamondShapeLabels}`);

        const diamondQualityLabels = getLabels(filters.diamondQuality, paramOptions.diamondQualities);
        if (diamondQualityLabels) appliedFilters.push(`Diamond Quality: ${diamondQualityLabels}`);

        const itemSizeLabels = getLabels(filters.itemSize, paramOptions.sizes);
        if (itemSizeLabels) appliedFilters.push(`Item Size: ${itemSizeLabels}`);

        const collectionLabels = getLabels(filters.collection, paramOptions.collections);
        if (collectionLabels) appliedFilters.push(`Collection: ${collectionLabels}`);

        const groupByText = noGrouping ? "" : groupBySelected.map(o => o.label).join(", ");

        const stateObj = {
            payload,
            reportName: selectedReport.name,
            reportType: noGrouping ? "Detail" : "Summary",
            appliedFilters,
            groupByText
        };
        const uniqueId = Date.now().toString();
        localStorage.setItem(`reportResultState_${uniqueId}`, JSON.stringify(stateObj));
        window.open(`/report-result?id=${uniqueId}`, "_blank");
    };

    const handleClearFilters = () => {
        setFilters({
            fromDate: null,
            toDate: null,
            styleNo: "",
            customer: [],
            category: [],
            metal: [],
            goldQuality: [],
            price: "",
            diamondShape: [],
            diamondQuality: [],
            itemSize: [],
            collection: []
        });
        setGroupBySelected([]);
    };

    const isCollapsed = !sidebarPinned && !sidebarHover;

    return (
        <div className="report-container">
            <ReportHeader 
                selectedReportPath={selectedReportPath} 
                reportType={reportType}
                setReportType={setReportType}
                noGrouping={noGrouping}
                setNoGrouping={setNoGrouping}
                onGenerateReport={handleGenerateReport}
                onClear={handleClearFilters}
            />
            <div className={`report-body ${isCollapsed ? "sidebar-collapsed" : ""}`}>
                <ReportListSection
                    reports={reports}
                    sidebarPinned={sidebarPinned}
                    setSidebarPinned={setSidebarPinned}
                    setSidebarHover={setSidebarHover}
                    setGroupByOptions={setGroupByOptions}
                    setGroupBySelected={setGroupBySelected}
                    setSelectedReportPath={setSelectedReportPath}
                    setReportType={setReportType}
                    onSelectReport={setSelectedReport}
                />
                <ReportLeftPanel 
                    filters={filters}
                    setFilters={setFilters}
                    paramOptions={paramOptions}
                    customers={customers}
                />
                <ReportRightPanel
                    groupByOptions={groupByOptions}
                    groupBySelected={groupBySelected}
                    setGroupBySelected={setGroupBySelected}
                />
            </div>
        </div>
    );
}